/* eslint-disable react/no-unused-prop-types */
import { FC, useRef, useState } from 'react';
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  Crop,
  makeAspectCrop,
  PixelCrop
} from 'react-image-crop';

import { Button } from '@components/index';

import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropModalProps {
  fileName: string;
  previewUrl: string;
  expectedAspectRatio: number;
  onClose: () => void;
  onConfirm: (croppedFile: File) => void;
}

const ImageCropModal: FC<ImageCropModalProps> = ({
  onClose,
  onConfirm,
  previewUrl,
  fileName,
  expectedAspectRatio
}) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 0,
    y: 0,
    width: 50,
    height: 28.125
  });

  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const handleImageLoad = (img: HTMLImageElement) => {
    imageRef.current = img;

    const percentCrop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 50
        },
        expectedAspectRatio,
        img.naturalWidth,
        img.naturalHeight
      ),
      img.naturalWidth,
      img.naturalHeight
    );
    const pixelCrop = convertToPixelCrop(
      percentCrop,
      img.naturalWidth / 2,
      img.naturalHeight / 2
    );

    setCrop(percentCrop);
    setCompletedCrop(pixelCrop as PixelCrop);
  };

  const handleCompleteCrop = (currentCrop: PixelCrop) => {
    setCompletedCrop(currentCrop);
  };

  const createCroppedImage = async (
    cropRegion: PixelCrop
  ): Promise<File | null> => {
    if (!cropRegion || !imageRef.current || !canvasRef.current) {
      return null;
    }

    const canvas = canvasRef.current;
    const image = imageRef.current;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const pixelRatio = window.devicePixelRatio;

    canvas.width = cropRegion.width * pixelRatio * scaleX;
    canvas.height = cropRegion.height * pixelRatio * scaleY;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      cropRegion.x * scaleX,
      cropRegion.y * scaleY,
      cropRegion.width * scaleX,
      cropRegion.height * scaleY,
      0,
      0,
      canvas.width / pixelRatio,
      canvas.height / pixelRatio
    );

    return new Promise<File | null>((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) return resolve(null);
        return resolve(new File([blob], fileName, { type: blob.type }));
      }, 'image/png');
    });
  };

  const handleConfirm = async () => {
    if (completedCrop) {
      const croppedFile = await createCroppedImage(completedCrop);
      if (croppedFile) {
        onConfirm(croppedFile);
      }
    }
  };

  return (
    <div className="gap-4">
      <div className="text-20 font-medium text-content">
        <div className="flex flex-col">Crop Organization Logo</div>
        <span className="text-sm font-medium text-gray-500">
          Drag to move and resize the crop area
        </span>
      </div>

      {previewUrl && (
        <div className="flex items-center justify-center">
          <ReactCrop
            crop={crop}
            aspect={expectedAspectRatio}
            onComplete={handleCompleteCrop}
            onChange={(newCrop) => setCrop(newCrop)}
          >
            <img
              src={previewUrl}
              className="!max-w-300 !max-h-[300px]"
              alt="Preview"
              onLoad={(e) => handleImageLoad(e.currentTarget)}
            />
          </ReactCrop>
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <div className="mt-4 flex gap-4">
        <Button
          label="Cancel"
          variant="secondary"
          className="w-105"
          handleClick={onClose}
        />
        <Button
          label="Save"
          variant="primary"
          className="w-105"
          handleClick={handleConfirm}
        />
      </div>
    </div>
  );
};

export default ImageCropModal;
